import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Select,
  MenuItem,
  Skeleton,
  Typography,
  Chip,
  useMediaQuery,
  useColorScheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Flag from "react-flagkit";
import { getCountry } from "countries-and-timezones";
import {
  getCountryData,
  FLAGKIT_SUPPORTED,
  getCountryFlagEmoji
} from "../../helpers/localization/countries";
import { BASE_URL, PROXY_SERVER, useProxy } from "../../constants/common";
import { useScreenSize } from "../../hooks/useScreenSize";

const DEFAULT_LNG = "en-US";

interface ILanguageSelectorProps {
  show: boolean;
  client: string;
}

export const LanguageSelector: React.FC<ILanguageSelectorProps> = ({
  show,
  client
}) => {
  const { i18n, t } = useTranslation();
  const { theme, isSmallScreen } = useScreenSize();
  const { mode: themeVariant } = useColorScheme();

  const showRegions = useMediaQuery(
    theme.breakpoints.up(theme.breakpoints.values.desktop + 50)
  );

  const [allAvailableLanguagesState, setAllAvailableLanguagesState] = useState<
    { value?: string; key?: string; name?: string }[]
  >([]);
  const [selectedLanguageState, setSelectedLanguageState] = useState<
    string | null
  >(null);

  useEffect(() => {
    const defaultLng = DEFAULT_LNG;
    const defaultCountryData = getCountryData(defaultLng);
    const defaultOptions = [
      {
        key: defaultLng,
        value: defaultCountryData?.code,
        name: defaultCountryData?.name
      }
    ];
    try {
      if (client) {
        axios
          .get(
            `${
              useProxy ? PROXY_SERVER : ""
            }${BASE_URL}/i18n/locales/enabled/${client}`,
            {
              timeout: 90000
            }
          )
          .then(({ data }) => {
            if (data?.enabledLanguages?.length === 0) {
              throw Error("no enabled languages");
            }

            const lngSelectorOptions =
              data?.enabledLanguages
                ?.map((lang: string) => {
                  const countryData = getCountryData(lang);
                  const countryCode = lang?.split("-")?.[1];
                  return {
                    key: lang,
                    label: lang,
                    value: countryData?.code ?? lang?.split("-")?.[0],
                    name:
                      countryData?.name ??
                      (countryCode
                        ? getCountry(countryCode)?.name
                        : undefined) ??
                      lang
                  };
                })
                ?.sort((a: { name: string }, b: { name: string }) => {
                  return a.name < b.name ? -1 : 1;
                }) ?? defaultOptions;

            if (data?.tmsIdentifier) {
              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
              (i18n.options as Record<string, any>).customerId =
                data?.tmsIdentifier; // force assertion as any for options as we need to attach customerId on typed option property for legacy reasons, without this ts code won't compile
            }

            setAllAvailableLanguagesState(lngSelectorOptions);
            setSelectedLanguageState(
              lngSelectorOptions.find(
                (lng: { key: string }) => lng?.key === i18n.language
              )?.key ?? defaultLng
            );
          })
          .catch(() => {
            setAllAvailableLanguagesState(defaultOptions);
            setSelectedLanguageState(defaultLng);
          });
      } else {
        setAllAvailableLanguagesState(defaultOptions);
        setSelectedLanguageState(defaultLng);
      }
    } catch (e) {
      console.error("Unable to fetch enabled languages", e);
    }
  }, [client]);

  useEffect(() => {
    if (selectedLanguageState) {
      i18n.reloadResources(selectedLanguageState).then(() => {
        i18n.changeLanguage(selectedLanguageState);
      });
    }
  }, [selectedLanguageState, i18n]);

  return show ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        backgroundColor: themeVariant === "light" ? "#FFFFFF" : "#2f2f2f",
        borderRadius: "4px",
        border: `1px solid ${themeVariant === "light" ? "#e0e0e0" : "#5a5a5a"}`
      }}
    >
      {allAvailableLanguagesState.length === 0 && !selectedLanguageState && (
        <Chip
          label={<Skeleton variant="text" width={"110px"} />}
          color="default"
          size="small"
          sx={{ width: "140px", height: "30px", borderRadius: "4px" }}
        />
      )}
      {allAvailableLanguagesState.length > 0 && selectedLanguageState && (
        <Select
          autoWidth
          value={selectedLanguageState}
          onChange={(ev) => {
            setSelectedLanguageState(ev.target.value);
          }}
          size="small"
          variant="outlined"
          inputProps={{
            "aria-label": t("ROOSTER.PUBLIC-APP.SELECT_LANGUAGE", {
              defaultValue: "Select language"
            })
          }}
          sx={{
            height: "30px",
            backgroundColor: themeVariant === "light" ? "#FFFFFF" : "#2f2f2f",
            color: themeVariant === "light" ? "#202020" : "#ffffff",
            border: "none",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none"
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none"
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none"
            },
            "& .MuiSelect-icon": {
              color: themeVariant === "light" ? "#202020" : "#ffffff"
            }
          }}
          renderValue={(selected) => {
            const selectedLanguage = allAvailableLanguagesState.find(
              (l) => l.key === selected
            );
            return (
              <Box
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  display: "flex",
                  height: "26px"
                }}
              >
                <span aria-hidden="true">
                  {FLAGKIT_SUPPORTED.has(selected?.split("-")?.[1] ?? "US") ? (
                    <Flag country={selected?.split("-")?.[1] ?? "US"} size={16} />
                  ) : (
                    <span role="img" style={{ fontSize: "16px", lineHeight: 1 }}>
                      {getCountryFlagEmoji(selected?.split("-")?.[1] ?? "US")}
                    </span>
                  )}
                </span>
                {!isSmallScreen ? (
                  <Typography fontSize="13px" sx={{ ml: 1 }}>
                    {selectedLanguage?.name}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      position: "absolute",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                      clip: "rect(0, 0, 0, 0)",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {selectedLanguage?.name}
                  </Typography>
                )}
              </Box>
            );
          }}
          MenuProps={{
            disablePortal: true,
            PaperProps: {
              sx: {
                backgroundColor:
                  themeVariant === "light" ? "#FFFFFF" : "#2f2f2f",
                color: themeVariant === "light" ? "#202020" : "#ffffff",
                border: `1px solid ${themeVariant === "light" ? "#e0e0e0" : "#5a5a5a"}`
              }
            }
          }}
        >
          {allAvailableLanguagesState.map((l) => (
            <MenuItem
              value={l.key}
              key={l.key}
              sx={{
                height: "32px",
                color: themeVariant === "light" ? "#202020" : "#ffffff",
                "&:hover": {
                  backgroundColor:
                    themeVariant === "light" ? "#f5f5f5" : "#3a3a3a"
                },
                "&.Mui-selected": {
                  backgroundColor:
                    themeVariant === "light" ? "#EAEAEA" : "#383838",
                  "&:hover": {
                    backgroundColor:
                      themeVariant === "light" ? "#e0e0e0" : "#404040"
                  }
                }
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  display: "flex",
                  height: "26px"
                }}
              >
                <span aria-hidden="true">
                  {FLAGKIT_SUPPORTED.has(l.key?.split("-")?.[1] ?? "US") ? (
                    <Flag country={l.key?.split("-")?.[1] ?? "US"} size={16} />
                  ) : (
                    <span role="img" style={{ fontSize: "16px", lineHeight: 1 }}>
                      {getCountryFlagEmoji(l.key?.split("-")?.[1] ?? "US")}
                    </span>
                  )}
                </span>{" "}
                <Typography fontSize="13px" textAlign="center">
                  &nbsp;&nbsp;
                  {l.name}{" "}
                  {showRegions &&
                    `(${
                      getCountry(l.key?.split("-")?.[1] ?? "")?.name ??
                      "Unknown Region"
                    })`}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  ) : (
    <></>
  );
};
